import {
  CreateSamlProviderDocument,
  type SecuritySettingsSamlSetupModalDomainFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { type FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'

import { Vendor } from '../types'

interface SamlFormValues {
  domainIds: SecuritySettingsSamlSetupModalDomainFragment['id'][]
  vendor?: Vendor
  metadata?: string

  droppedFile?: File
}

export type SamlForm = ReturnType<typeof useForm<SamlFormValues>>

export function useSamlForm(
  initialValues: SamlFormValues,
  afterSubmit: (success: boolean) => void
) {
  const session = useCurrentSession()
  const [createProvider] = useMutation(CreateSamlProviderDocument, {
    extractorFn: (data) => data.cognitoMutations.createCognitoSamlProvider,
  })

  const form = useForm<SamlFormValues>({
    defaultValues: initialValues,
    validate: (data) => {
      const errors: FormErrors<SamlFormValues> = {}
      // No need to localize these errors since they're only here for validation purposes.
      // We guide the user through each step of the flow, so these are never presented in the UI.
      if (!data.domainIds || data.domainIds.length === 0) errors.domainIds = 'domainIds is required'
      if (!data.vendor) errors.vendor = 'vendor is required'
      if (!data.metadata) errors.metadata = 'metadata is required'
      return errors
    },
    onSubmit: async (data, leForm) => {
      leForm.setSubmitting(true)
      const result = handleResult(
        await createProvider({
          variables: {
            companyId: session.company.id,
            domainIds: data.domainIds,
            vendor: data.vendor!, // eslint-disable-line no-type-assertion/no-type-assertion
            samlMetadata: data.metadata!, // eslint-disable-line no-type-assertion/no-type-assertion
          },
        }),
        (_errors) => {
          // We're handling the errors in the parent component since we have local state there...
          // FIXME: Migrate the parent to GraphQL and handle the errors here.
        }
      )
      leForm.setSubmitting(false)
      afterSubmit(result.success)
      return result
    },
  })

  return form
}
